import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useRef, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { AlertTriangle, ChevronDown, ChevronRight, ExternalLink, MessageSquare, HelpCircle, GripVertical, X, ArrowUpDown, Inbox, RotateCcw, Rocket } from "lucide-react"
import { PageHeader } from "@/components/portal/PageHeader"
import { Loading, ErrorBox, fmtRelative } from "@/components/portal/LoadingError"
import { toast } from "@/components/portal/Toast"
import { ModalShell } from "@/components/portal/ModalShell"
import { umsetzungHinweis } from "@/lib/backlogUmsetzung"

/**
 * Backlog — Spiegel der GitHub Issues aus zwei Repos (Plattform + Voice-Bot).
 *
 * Aenderbar sind drei Dinge: Prioritaet, Schliessen, Reihenfolge. Prioritaet und
 * Schliessen gehen zuerst nach GitHub und werden erst danach hier nachgezogen —
 * eine Aenderung nur im Spiegel waere beim naechsten Sync still wieder weg. Die
 * Reihenfolge ist reine Portal-Information, GitHub kennt keine Sortierung.
 *
 * Wichtig ist das Frische-Banner: eine leere Liste kann "nichts offen" heissen
 * oder "der Sync ist tot". Diese beiden Faelle duerfen nie gleich aussehen.
 */

type QuellenStatus = {
  repo: string
  quelle: string
  veraltet: boolean
  last_ok_at: string | null
  last_error: string | null
}

type Sync = {
  konfiguriert: boolean
  veraltet: boolean
  quellen: QuellenStatus[]
  hinweis: string | null
}

type Item = {
  repo: string
  quelle: string | null
  number: number
  title: string
  state: string
  labels: string[]
  prioritaet: string | null
  art: string | null
  rueckfrage: boolean
  autor: string | null
  comments_count: number
  html_url: string | null
  created_at: string | null
  updated_at: string | null
  body_kurz: string
  umgesetzt?: boolean
  umgesetzt_am?: string | null
  wirkt_nicht?: boolean
}

type Eingang = {
  quelle: string | null
  channel: string
  ts: string
  text: string
  empfangen_at: string | null
  permalink: string
  herkunft?: string
  melder?: string | null
  kontext?: Record<string, unknown> | null
  hat_bild?: boolean
  bild_anzahl?: number
  bild_beschreibung?: string | null
  bild_status?: string | null
}

/**
 * Eine Meldung im Detail — Ziel des Links aus dem Ticket (`/backlog?meldung=<ts>`).
 *
 * 🔴 Nur hier, mit Login, ist das Bild zu sehen (geschwärztes Portal-
 * Bildschirmfoto bzw. Slack-Screenshot, 30 Tage). Der Triage-Lauf (KI) bekommt
 * es nie — er sieht Kontext und Bildbeschreibung.
 */
function MeldungAnsicht({ channel, ts, onClose }: { channel: string; ts: string; onClose: () => void }) {
  const m = useQuery<Eingang>({
    queryKey: ["backlog-meldung", channel, ts],
    queryFn: async () => {
      const r = await fetch(`/api/v1/backlog/meldung/${encodeURIComponent(channel)}/${encodeURIComponent(ts)}`,
        { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json()
    },
  })
  const d = m.data
  return (
    <ModalShell open onClose={onClose} breite={760} titel="Meldung"
      untertitel={d ? `${d.herkunft === "portal" ? "Aus dem Portal" : "Aus Slack"}${d.melder ? ` · ${d.melder}` : ""} · ${fmtRelative(d.empfangen_at)}` : undefined}>
      <div style={{ padding: "14px 18px", display: "flex", flexDirection: "column", gap: 12, fontSize: 13 }}>
        {m.error ? <ErrorBox error={m.error} /> : !d ? <Loading label="Lädt..." /> : (
          <>
            <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.5 }}>{d.text}</div>
            {d.kontext && (
              <details open>
                <summary style={{ cursor: "pointer" }}>Kontext</summary>
                <pre style={{ fontSize: 11.5, background: "#f8fafc", padding: 10, borderRadius: 6, overflowX: "auto", whiteSpace: "pre-wrap" }}>
                  {JSON.stringify(d.kontext, null, 2)}
                </pre>
              </details>
            )}
            {d.bild_beschreibung && (
              <div>
                <strong>Bildbeschreibung (ohne Personendaten)</strong>
                <div style={{ whiteSpace: "pre-wrap", color: "#334155", marginTop: 4 }}>{d.bild_beschreibung}</div>
              </div>
            )}
            {Array.from({ length: d.bild_anzahl || 0 }, (_, i) => (
              <img key={i} alt={`Bild ${i + 1}`}
                src={`/api/v1/backlog/meldung/${encodeURIComponent(channel)}/${encodeURIComponent(ts)}/bild?nr=${i + 1}`}
                style={{ maxWidth: "100%", border: "1px solid #e5e7eb", borderRadius: 6 }} />
            ))}
            {!d.bild_anzahl && d.hat_bild && (
              <div style={{ color: "#64748b" }}>Bild nicht (mehr) gespeichert — Bilder werden nach 30 Tagen gelöscht.</div>
            )}
          </>
        )}
      </div>
    </ModalShell>
  )
}

type Liste = {
  items: Item[]
  shown: number
  total: number
  counts: Record<string, number>
  quellen: Record<string, string>
  handsortiert: boolean
  eingang: Eingang[]
  eingang_gesamt: number
  sync: Sync
}

type Kommentar = {
  id: number
  autor: string | null
  body: string
  created_at: string | null
}

type Detail = Item & { body: string; kommentare: Kommentar[] }

const PRIO_FARBE: Record<string, { bg: string; fg: string; label: string }> = {
  P0: { bg: "#7f1d1d", fg: "#fecaca", label: "P0 — sofort" },
  P1: { bg: "#7c2d12", fg: "#fed7aa", label: "P1 — blockiert, kein Workaround" },
  P2: { bg: "#713f12", fg: "#fde68a", label: "P2 — Workaround vorhanden" },
  P3: { bg: "#1e3a5f", fg: "#bfdbfe", label: "P3 — Verbesserung" },
}

async function fetchListe(state: string, prio: string, art: string, repo: string, q: string): Promise<Liste> {
  const p = new URLSearchParams({ state })
  if (prio) p.set("prioritaet", prio)
  if (art) p.set("art", art)
  if (repo) p.set("repo", repo)
  if (q.trim()) p.set("q", q.trim())
  const r = await fetch(`/api/v1/backlog?${p}`, { credentials: "include" })
  if (!r.ok) throw new Error(`HTTP ${r.status}`)
  return r.json()
}

async function fetchDetail(repo: string, nr: number): Promise<Detail> {
  const r = await fetch(`/api/v1/backlog/${repo}/${nr}`, { credentials: "include" })
  if (!r.ok) throw new Error(`HTTP ${r.status}`)
  return r.json()
}

async function schreibe(pfad: string, methode: string, body?: unknown) {
  const r = await fetch(`/api/v1/backlog${pfad}`, {
    method: methode,
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  })
  if (!r.ok) {
    // 409 traegt einen Text fuer Menschen (z.B. "Token darf nicht schreiben").
    // Der muss durch bis zum Nutzer — sonst klickt er ratlos weiter.
    let text = `HTTP ${r.status}`
    try { text = (await r.json())?.detail || text } catch { /* egal */ }
    throw new Error(text)
  }
  return r.json()
}

/**
 * Eingang — erfasst, aber noch nicht bewertet.
 *
 * Ohne diesen Block sieht die Seite aus, als waere eine Meldung gar nicht
 * angekommen, dabei liegt sie nur vor der Triage. Genau diese Luecke hat zu der
 * Frage gefuehrt "warum sehe ich mein Ticket nicht?".
 */
function EingangBlock({ eintraege, onAnsehen }: { eintraege: Eingang[]; onAnsehen: (e: Eingang) => void }) {
  const [offen, setOffen] = useState(true)
  const qc = useQueryClient()
  const auffrischen = () => qc.invalidateQueries({ queryKey: ["backlog"] })

  // Verwerfen ist der einzige Weg, der NICHT von allein passiert: was ein
  // Ticket bekommt, raeumt der Sync selbst ab. Geplauder muss jemand
  // wegklicken — deshalb hier und nicht in einem Skript auf einem Laptop.
  const verwerfen = useMutation({
    mutationFn: (e: Eingang) =>
      schreibe("/eingang/verwerfen", "POST", { channel: e.channel, ts: e.ts }),
    onSuccess: (_d, e) => {
      auffrischen()
      // Mit Rueckgaengig-Knopf: ein Fehlgriff waere sonst nur noch per SQL
      // einzufangen, und eine verlorene Meldung kostet mehr als ein Ticket
      // zu viel.
      toast("Aus dem Eingang genommen", "success", {
        label: "Rueckgaengig",
        onClick: () => {
          schreibe("/eingang/zurueckholen", "POST", { channel: e.channel, ts: e.ts })
            .then(auffrischen)
            .catch((err) => toast(String(err.message || err), "error"))
        },
      })
    },
    onError: (err: Error) => toast(err.message, "error"),
  })

  if (eintraege.length === 0) return null

  return (
    <div style={{
      marginBottom: 18, borderRadius: 6, overflow: "hidden",
      border: "1px solid var(--border, #334155)",
    }}>
      <div
        onClick={() => setOffen((o) => !o)}
        style={{
          display: "flex", alignItems: "center", gap: 8, cursor: "pointer",
          padding: "8px 12px", background: "var(--surface-2, #1e293b)",
        }}
      >
        {offen ? <ChevronDown className="w-4 h-4" style={{ opacity: 0.6 }} />
               : <ChevronRight className="w-4 h-4" style={{ opacity: 0.6 }} />}
        <Inbox className="w-4 h-4" style={{ opacity: 0.7 }} />
        <strong style={{ fontSize: 13 }}>Eingang</strong>
        <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
          {eintraege.length} {eintraege.length === 1 ? "Meldung" : "Meldungen"} aus Slack und Portal,
          noch nicht bewertet
        </span>
      </div>

      {offen && (
        <div style={{ padding: "4px 12px 10px" }}>
          <div style={{ fontSize: 12, color: "var(--text-muted)", margin: "6px 0 10px", lineHeight: 1.5 }}>
            Angekommen und gespeichert. Beim naechsten Triage-Lauf wird daraus ein Ticket,
            eine Ergaenzung an einem bestehenden — oder es faellt als Geplauder raus.
            Sobald ein Ticket den Slack-Link nennt, verschwindet die Zeile hier von
            selbst. Geplauder nimmst du mit dem ✕ heraus.
          </div>
          {eintraege.map((e) => (
            <div key={`${e.channel}-${e.ts}`} style={{
              display: "flex", gap: 10, alignItems: "flex-start",
              padding: "6px 0", borderTop: "1px solid var(--border, #1e293b)",
            }}>
              <span style={{ fontSize: 12, color: "var(--text-muted)", width: 78, flexShrink: 0, paddingTop: 2 }}>
                {fmtRelative(e.empfangen_at)}
              </span>
              {e.quelle && (
                <span style={{
                  fontSize: 12, padding: "1px 6px", borderRadius: 3, whiteSpace: "nowrap",
                  border: "1px solid var(--border, #334155)", color: "var(--text-muted)", flexShrink: 0,
                }}>
                  {e.herkunft === "portal" ? `Portal${e.melder ? ` · ${e.melder}` : ""}` : e.quelle}
                </span>
              )}
              <span style={{ fontSize: 13, flex: 1, minWidth: 0, lineHeight: 1.45 }}>{e.text}</span>
              {(e.herkunft === "portal" || e.hat_bild) && (
                <button
                  type="button"
                  title={e.hat_bild ? "Meldung mit Bild ansehen" : "Meldung ansehen"}
                  onClick={() => onAnsehen(e)}
                  style={{ background: "none", border: "none", cursor: "pointer", padding: 0, color: "var(--text-muted)", flexShrink: 0, paddingTop: 2, fontSize: 12 }}
                >
                  {e.hat_bild ? "Bild" : "Details"}
                </button>
              )}
              {e.herkunft !== "portal" && (
                <a
                  href={e.permalink}
                  target="_blank"
                  rel="noreferrer"
                  title="In Slack oeffnen"
                  style={{ color: "var(--text-muted)", flexShrink: 0, paddingTop: 2 }}
                >
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
              <button
                type="button"
                title="Kein Ticket daraus — aus dem Eingang nehmen"
                disabled={verwerfen.isPending}
                onClick={() => {
                  const kurz = e.text.length > 120 ? e.text.slice(0, 120) + "…" : e.text
                  if (!confirm(`Aus dem Eingang nehmen, ohne Ticket?\n\n„${kurz}"`)) return
                  verwerfen.mutate(e)
                }}
                style={{
                  background: "none", border: "none", cursor: "pointer", padding: 0,
                  color: "var(--text-muted)", flexShrink: 0, paddingTop: 2,
                  opacity: verwerfen.isPending ? 0.4 : 1,
                }}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/** Frische-Banner. Erscheint nur, wenn etwas nicht stimmt — und benennt, WELCHE
 *  Quelle klemmt: faellt nur der Voice-Spiegel aus, sind die Plattform-Tickets
 *  weiterhin aktuell. */
function SyncBanner({ sync }: { sync: Sync }) {
  if (sync.konfiguriert && !sync.veraltet) return null
  const kaputt = sync.quellen.filter((q) => q.veraltet)
  return (
    <div style={{
      display: "flex", gap: 10, alignItems: "flex-start", padding: "10px 14px",
      borderRadius: 6, marginBottom: 16,
      background: "rgba(180, 83, 9, 0.15)", border: "1px solid rgba(180, 83, 9, 0.4)",
    }}>
      <AlertTriangle className="w-4 h-4" style={{ color: "#fbbf24", flexShrink: 0, marginTop: 2 }} />
      <div style={{ fontSize: 13, lineHeight: 1.5 }}>
        <strong style={{ color: "#fbbf24" }}>
          {!sync.konfiguriert
            ? "Backlog-Spiegel nicht eingerichtet"
            : kaputt.length === sync.quellen.length
              ? "Backlog-Spiegel ist nicht aktuell"
              : `Eine Quelle ist nicht aktuell: ${kaputt.map((q) => q.quelle).join(", ")}`}
        </strong>
        <div style={{ color: "var(--text-muted)", marginTop: 3 }}>
          {sync.hinweis && <div>{sync.hinweis}</div>}
          {kaputt.map((q) => (
            <div key={q.repo} style={{ marginTop: 3 }}>
              <strong>{q.quelle}:</strong>{" "}
              {q.last_ok_at
                ? <>letzter Abgleich {fmtRelative(q.last_ok_at)} — diese Eintraege koennen alt sein</>
                : <>noch nie abgeglichen — nicht als &laquo;nichts offen&raquo; lesen</>}
              {q.last_error && (
                <div style={{ fontFamily: "monospace", fontSize: 12, marginTop: 2 }}>{q.last_error}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/** Prioritaet aendern — als Auswahl direkt in der Zeile. */
function PrioAuswahl({ item, onFehler }: { item: Item; onFehler: (m: string) => void }) {
  const qc = useQueryClient()
  const m = useMutation({
    mutationFn: (prioritaet: string | null) =>
      schreibe(`/${item.repo}/${item.number}`, "PATCH", { prioritaet }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["backlog"] }),
    onError: (e: Error) => onFehler(e.message),
  })

  // Die Farbe steckt in der Auswahl selbst — sonst muesste man zum Erkennen
  // der Dringlichkeit lesen statt hinzusehen, und P0 ginge in der Liste unter.
  const f = item.prioritaet ? PRIO_FARBE[item.prioritaet] : undefined

  return (
    <select
      value={item.prioritaet || ""}
      disabled={m.isPending}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => m.mutate(e.target.value || null)}
      title={f ? `${f.label} — zum Aendern klicken` : "Prioritaet setzen"}
      className="pp-input"
      style={{
        fontSize: 12, fontWeight: 700, padding: "1px 4px", width: 62,
        background: f ? f.bg : "var(--surface-2, #1e293b)",
        color: f ? f.fg : "var(--text-muted)",
        border: "none", borderRadius: 4,
        opacity: m.isPending ? 0.5 : 1, cursor: "pointer",
      }}
    >
      <option value="">ohne</option>
      <option value="P0">P0</option>
      <option value="P1">P1</option>
      <option value="P2">P2</option>
      <option value="P3">P3</option>
    </select>
  )
}

function Zeile({
  item, mehrereQuellen, ziehbar, onDragStart, onDragOver, onDrop, wirdGezogen,
}: {
  item: Item
  mehrereQuellen: boolean
  ziehbar: boolean
  onDragStart: () => void
  onDragOver: (e: React.DragEvent) => void
  onDrop: () => void
  wirdGezogen: boolean
}) {
  const [offen, setOffen] = useState(false)
  const qc = useQueryClient()
  const detail = useQuery({
    queryKey: ["backlog-detail", item.repo, item.number],
    queryFn: () => fetchDetail(item.repo, item.number),
    enabled: offen,
    staleTime: 30_000,
  })

  const schliessen = useMutation({
    mutationFn: () => schreibe(`/${item.repo}/${item.number}/schliessen`, "POST"),
    onSuccess: () => {
      toast(`#${item.number} aus der Liste genommen`, "success")
      qc.invalidateQueries({ queryKey: ["backlog"] })
    },
    onError: (e: Error) => toast(e.message, "warn"),
  })

  // Gegenstueck zum Herausnehmen. Ohne diesen Knopf waere "aus der Liste
  // nehmen" faktisch endgueltig — zurueckholen ginge nur in GitHub, und dorthin
  // kommt ausser Paul niemand.
  const oeffnen = useMutation({
    mutationFn: () => schreibe(`/${item.repo}/${item.number}/oeffnen`, "POST"),
    onSuccess: () => {
      toast(`#${item.number} wieder geoeffnet`, "success")
      qc.invalidateQueries({ queryKey: ["backlog"] })
    },
    onError: (e: Error) => toast(e.message, "warn"),
  })

  return (
    <div
      draggable={ziehbar}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      style={{
        borderBottom: "1px solid var(--border, #1e293b)",
        opacity: wirdGezogen ? 0.4 : 1,
        background: wirdGezogen ? "var(--surface-2, #1e293b)" : undefined,
      }}
    >
      <div
        onClick={() => setOffen((o) => !o)}
        style={{ display: "flex", gap: 8, alignItems: "center", padding: "10px 4px", cursor: "pointer" }}
      >
        <GripVertical
          className="w-4 h-4"
          style={{ flexShrink: 0, opacity: 0.3, cursor: ziehbar ? "grab" : "default" }}
          onClick={(e) => e.stopPropagation()}
        />
        {offen ? <ChevronDown className="w-4 h-4" style={{ flexShrink: 0, opacity: 0.5 }} />
               : <ChevronRight className="w-4 h-4" style={{ flexShrink: 0, opacity: 0.5 }} />}
        <PrioAuswahl item={item} onFehler={(m) => toast(m, "warn")} />
        {mehrereQuellen && item.quelle && (
          <span style={{
            fontSize: 12, padding: "1px 6px", borderRadius: 3, whiteSpace: "nowrap",
            border: "1px solid var(--border, #334155)", color: "var(--text-muted)",
          }}>
            {item.quelle}
          </span>
        )}
        {/* Die Nummer steht bewusst in der Zeile: ohne sie kann man ein Ticket
            nur ueber seinen Titel benennen, und den sieht jeder anders. */}
        <span
          title="Ticket-Nummer"
          style={{
            fontSize: 12, fontVariantNumeric: "tabular-nums", flexShrink: 0,
            color: "var(--text-muted)",
            // Feste Breite, rechtsbuendig: sonst beginnen die Titel je nach
            // Stellenzahl der Nummer an unterschiedlichen Stellen.
            minWidth: 34, textAlign: "right",
            opacity: item.state === "closed" ? 0.6 : 1,
          }}
        >
          #{item.number}
        </span>
        <span style={{
          fontSize: 13, fontWeight: 500, flex: 1, minWidth: 0,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          textDecoration: item.state === "closed" ? "line-through" : undefined,
          opacity: item.state === "closed" ? 0.6 : 1,
        }}>
          {item.title}
        </span>
        {item.rueckfrage && (
          <span title="Rueckfrage im Slack-Thread laeuft noch" style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 12, color: "#c4b5fd" }}>
            <HelpCircle className="w-3.5 h-3.5" /> Rueckfrage
          </span>
        )}
        {(() => {
          const h = umsetzungHinweis(item)
          return h && (
            <span title={h.titel} style={{
              display: "inline-flex", alignItems: "center", gap: 3, fontSize: 12, whiteSpace: "nowrap",
              color: h.wirktNicht ? "#d97706" : "#16a34a",
            }}>
              <Rocket className="w-3.5 h-3.5" /> {h.text}
            </span>
          )
        })()}
        {item.art && <span style={{ fontSize: 12, color: "var(--text-muted)" }}>{item.art}</span>}
        {item.comments_count > 0 && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 12, color: "var(--text-muted)" }}>
            <MessageSquare className="w-3.5 h-3.5" /> {item.comments_count}
          </span>
        )}
        <span style={{ fontSize: 12, color: "var(--text-muted)", width: 84, textAlign: "right" }}>
          {fmtRelative(item.updated_at)}
        </span>
        {item.state === "closed" ? (
          <button
            type="button"
            title="Wieder in die Liste aufnehmen"
            disabled={oeffnen.isPending}
            onClick={(e) => { e.stopPropagation(); oeffnen.mutate() }}
            className="pp-btn pp-btn-sm"
            style={{ padding: "2px 7px", fontSize: 12, opacity: oeffnen.isPending ? 0.5 : 0.85 }}
          >
            <RotateCcw className="w-3.5 h-3.5" /> Wieder oeffnen
          </button>
        ) : (
          <button
            type="button"
            title="Aus der Liste nehmen (bleibt in GitHub erhalten)"
            disabled={schliessen.isPending}
            onClick={(e) => {
              e.stopPropagation()
              // Bewusst mit Rueckfrage: das Ticket verschwindet fuer alle,
              // nicht nur fuer den, der geklickt hat.
              if (confirm(`#${item.number} „${item.title}" aus der Liste nehmen?\n\nDas Ticket wird in GitHub geschlossen, nicht geloescht — es laesst sich zurueckholen.`)) {
                schliessen.mutate()
              }
            }}
            className="pp-btn pp-btn-sm"
            style={{ padding: "2px 5px", opacity: schliessen.isPending ? 0.5 : 0.6 }}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {offen && (
        <div style={{ padding: "4px 4px 16px 34px" }}>
          {detail.isLoading ? (
            <Loading label="Laedt..." />
          ) : detail.error || !detail.data ? (
            <ErrorBox error={detail.error} />
          ) : (
            <>
              <div style={{
                fontSize: 13, lineHeight: 1.6, whiteSpace: "pre-wrap",
                color: "var(--text-secondary, #cbd5e1)", maxWidth: 800,
              }}>
                {detail.data.body || <em style={{ color: "var(--text-muted)" }}>keine Beschreibung</em>}
              </div>

              {detail.data.kommentare.length > 0 && (
                <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 10 }}>
                  {detail.data.kommentare.map((k) => (
                    <div key={k.id} style={{ borderLeft: "2px solid var(--border, #1e293b)", paddingLeft: 12 }}>
                      <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 3 }}>
                        {k.autor} · {fmtRelative(k.created_at)}
                      </div>
                      <div style={{ fontSize: 13, lineHeight: 1.6, whiteSpace: "pre-wrap", maxWidth: 800 }}>
                        {k.body}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div style={{ marginTop: 14, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                {detail.data.labels.map((l) => (
                  <span key={l} style={{
                    fontSize: 12, padding: "1px 7px", borderRadius: 10,
                    border: "1px solid var(--border, #334155)", color: "var(--text-muted)",
                  }}>{l}</span>
                ))}
                {detail.data.html_url && (
                  <a
                    href={detail.data.html_url}
                    target="_blank"
                    rel="noreferrer"
                    style={{ fontSize: 12, color: "var(--text-muted)", display: "inline-flex", alignItems: "center", gap: 4 }}
                    title="Nur mit GitHub-Zugang"
                  >
                    <ExternalLink className="w-3 h-3" /> #{detail.data.number} in GitHub
                  </a>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default function BacklogPage() {
  const [state, setState] = useState("open")
  // Link aus einem Ticket: /backlog?meldung=<ts> (Portal-Meldung, Baustein 17)
  const [suche, setSuche] = useSearchParams()
  const meldungTs = suche.get("meldung")
  const [prio, setPrio] = useState("")
  const [art, setArt] = useState("")
  const [repo, setRepo] = useState("")
  const [q, setQ] = useState("")
  const qc = useQueryClient()

  const liste = useQuery({
    queryKey: ["backlog", state, prio, art, repo, q],
    queryFn: () => fetchListe(state, prio, art, repo, q),
    staleTime: 15_000,
  })

  // Eigene Kopie der Reihenfolge, damit das Ziehen sofort sichtbar ist und
  // nicht erst nach der Serverantwort ruckelt.
  const [reihenfolge, setReihenfolge] = useState<Item[]>([])
  const gezogen = useRef<number | null>(null)
  const [gezogenIdx, setGezogenIdx] = useState<number | null>(null)

  useEffect(() => {
    if (liste.data) setReihenfolge(liste.data.items)
  }, [liste.data])

  const speichern = useMutation({
    mutationFn: (items: Item[]) =>
      schreibe("/reihenfolge", "PUT", {
        eintraege: items.map((i, idx) => ({ repo: i.repo, number: i.number, position: idx })),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["backlog"] }),
    onError: (e: Error) => {
      toast(`Reihenfolge nicht gespeichert: ${e.message}`, "warn")
      // Zurueck auf den Serverstand — eine Sortierung, die nur lokal aussieht
      // als waere sie gespeichert, ist schlimmer als gar keine.
      if (liste.data) setReihenfolge(liste.data.items)
    },
  })

  const zuruecksetzen = useMutation({
    mutationFn: () => schreibe("/reihenfolge", "DELETE"),
    onSuccess: () => {
      toast("Sortierung nach Prioritaet wiederhergestellt", "success")
      qc.invalidateQueries({ queryKey: ["backlog"] })
    },
    onError: (e: Error) => toast(e.message, "warn"),
  })

  const ablegen = (zielIdx: number) => {
    const von = gezogen.current
    gezogen.current = null
    setGezogenIdx(null)
    if (von === null || von === zielIdx) return
    const neu = [...reihenfolge]
    const [bewegt] = neu.splice(von, 1)
    neu.splice(zielIdx, 0, bewegt)
    setReihenfolge(neu)
    speichern.mutate(neu)
  }

  const counts = liste.data?.counts || {}
  const quellen = liste.data?.quellen || {}
  // Ziehen nur in der ungefilterten Liste: sonst schiebt man Positionen
  // zwischen Eintraegen, die man gerade gar nicht sieht.
  const gefiltert = !!(prio || art || repo || q.trim()) || state !== "open"
  const ziehbar = !gefiltert

  return (
    <div className="px-10 py-8 max-w-[1200px]">
      <PageHeader
        title="Backlog"
        count={liste.data?.total}
        onRefresh={() => liste.refetch()}
        refreshing={liste.isFetching}
      >
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Suchen oder #Nummer..."
            className="pp-input"
            style={{ fontSize: 13, padding: "5px 10px", width: 160 }}
          />
          <select value={repo} onChange={(e) => setRepo(e.target.value)} className="pp-input" style={{ fontSize: 13, padding: "5px 10px" }}>
            <option value="">Alle Quellen</option>
            {Object.entries(quellen).map(([r, name]) => (
              <option key={r} value={r}>{name}</option>
            ))}
          </select>
          <select value={prio} onChange={(e) => setPrio(e.target.value)} className="pp-input" style={{ fontSize: 13, padding: "5px 10px" }}>
            <option value="">Alle Prioritaeten</option>
            <option value="P0">P0</option>
            <option value="P1">P1</option>
            <option value="P2">P2</option>
            <option value="P3">P3</option>
          </select>
          <select value={art} onChange={(e) => setArt(e.target.value)} className="pp-input" style={{ fontSize: 13, padding: "5px 10px" }}>
            <option value="">Bug + Verbesserung</option>
            <option value="bug">nur Bugs</option>
            <option value="verbesserung">nur Verbesserungen</option>
          </select>
          <select value={state} onChange={(e) => setState(e.target.value)} className="pp-input" style={{ fontSize: 13, padding: "5px 10px" }}>
            <option value="open">Offen</option>
            <option value="closed">Erledigt</option>
            <option value="all">Alle</option>
          </select>
        </div>
      </PageHeader>

      <p style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 4, marginBottom: 14, maxWidth: 780, lineHeight: 1.5 }}>
        Feedback aus Slack. Neue Meldungen landen sofort im <strong>Eingang</strong>; bewertet
        werden sie beim naechsten Triage-Lauf und stehen dann als Ticket in der Liste darunter.
        Prioritaet aendern, Eintraege herausnehmen und die Reihenfolge per Ziehen geht direkt hier.
      </p>

      {liste.data?.sync && <SyncBanner sync={liste.data.sync} />}

      {liste.data?.eingang && (
        <EingangBlock
          eintraege={liste.data.eingang}
          onAnsehen={(e) => setSuche((p) => {
            const n = new URLSearchParams(p)
            n.set("meldung", e.ts)
            if (e.channel !== "portal") n.set("kanal", e.channel); else n.delete("kanal")
            return n
          })}
        />
      )}
      {meldungTs && (
        <MeldungAnsicht
          channel={suche.get("kanal") || "portal"}
          ts={meldungTs}
          onClose={() => setSuche((p) => {
            const n = new URLSearchParams(p)
            n.delete("meldung"); n.delete("kanal")
            return n
          })}
        />
      )}

      <div style={{ display: "flex", gap: 14, marginBottom: 12, fontSize: 12, color: "var(--text-muted)", alignItems: "center", flexWrap: "wrap" }}>
        {["P0", "P1", "P2", "P3", "ohne"].map((p) => counts[p] ? (
          <span key={p}><strong style={{ color: "var(--text-primary, #e2e8f0)" }}>{counts[p]}</strong> {p}</span>
        ) : null)}
        {liste.data?.handsortiert && (
          <button
            type="button"
            onClick={() => zuruecksetzen.mutate()}
            disabled={zuruecksetzen.isPending}
            className="pp-btn pp-btn-sm"
            style={{ fontSize: 12, padding: "2px 7px", marginLeft: "auto" }}
            title="Handsortierung verwerfen und wieder nach Prioritaet sortieren"
          >
            <ArrowUpDown className="w-3 h-3" /> Sortierung zuruecksetzen
          </button>
        )}
      </div>

      {gefiltert && (
        <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 10 }}>
          Umsortieren geht nur in der ungefilterten Liste der offenen Eintraege — sonst
          verschiebst du Positionen zwischen Zeilen, die gerade nicht sichtbar sind.
        </div>
      )}

      {liste.error ? (
        <ErrorBox error={liste.error} />
      ) : liste.isLoading || !liste.data ? (
        <Loading label="Laedt..." />
      ) : reihenfolge.length === 0 ? (
        <div style={{ fontSize: 13, color: "var(--text-muted)", padding: "24px 0" }}>
          {liste.data.sync.konfiguriert && !liste.data.sync.veraltet
            ? "Nichts offen."
            : "Keine Eintraege — siehe Hinweis oben."}
        </div>
      ) : (
        <>
          <div>
            {reihenfolge.map((i, idx) => (
              <Zeile
                key={`${i.repo}#${i.number}`}
                item={i}
                mehrereQuellen={!repo}
                ziehbar={ziehbar}
                wirdGezogen={gezogenIdx === idx}
                onDragStart={() => { gezogen.current = idx; setGezogenIdx(idx) }}
                onDragOver={(e) => { if (ziehbar) e.preventDefault() }}
                onDrop={() => ablegen(idx)}
              />
            ))}
          </div>
          {liste.data.shown < liste.data.total && (
            <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 12 }}>
              {liste.data.shown} von {liste.data.total} angezeigt — Filter nutzen, um einzugrenzen.
            </div>
          )}
        </>
      )}
    </div>
  )
}
